import React from 'react';
import Pipeline from './Pipeline';
import { totalExperience } from '../utils/dateUtils';
import experience from '../data/experience';

import { scrollToId } from '../utils/navigation';
import { SITE_CONFIG } from '../config/site';

interface HeroProps {
  onRequestResume: () => void;
}

export default function Hero({ onRequestResume }: HeroProps) {
  const dynamicExp = totalExperience(experience);
  const linkedinUrl = import.meta.env.VITE_LINKEDIN_URL || SITE_CONFIG.defaultLinkedinUrl;
  const githubUrl = SITE_CONFIG.githubUrl;

  return (
    <section className="hero" id="top">
      <div className="wrap">
        <div className="hero-grid">
          <div className="hero-intro">
            <div className="eyebrow">
              <span className="live-dot"></span>
              Rugby &amp; Leeds, UK · Open to Enterprise Backend &amp; AI Roles
            </div>

            <h1 className="hero-title">
              Nagendra Vikram <span className="accent">Dixit</span>
            </h1>

            <p className="hero-headline-role">
              Enterprise AI Agent Builder · Automation Engineer · Senior Backend Engineer
            </p>

            <p className="hero-sub">
              Delivering production-grade AI agent workflows, resilient microservices, and enterprise automation pipelines for major UK retail infrastructure. Proven track record turning complex manual bottlenecks into deterministic, governed systems across Python, Java 21, AWS, LangGraph, and Kubernetes.
            </p>

            <div className="hero-tags">
              <span className="tag">LangGraph</span>
              <span className="tag">pgvector RAG</span>
              <span className="tag">Python · FastAPI</span>
              <span className="tag">Java 21</span>
              <span className="tag">AWS &amp; Kubernetes</span>
              <span className="tag">Kafka</span>
              <span className="tag">Keycloak RBAC</span>
            </div>

            <div className="hero-ctas">
              <button
                type="button"
                className="btn-primary"
                onClick={onRequestResume}
                aria-label="Request Full Résumé"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Request Full Résumé</span>
              </button>

              <a
                href="#flagship"
                className="btn-secondary scrolllink"
                onClick={(e) => { e.preventDefault(); scrollToId('flagship'); }}
              >
                Explore Flagship Agent
              </a>

              <div className="hero-social-links">
                <a
                  href={githubUrl}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="social-icon-btn"
                  aria-label="Nagendra Dixit's GitHub"
                  title="GitHub Profile"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
                  </svg>
                </a>
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="social-icon-btn"
                  aria-label="Nagendra Dixit's LinkedIn"
                  title="LinkedIn Profile"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="statuscard">
            <div className="head">
              <span className="pulse"></span>
              <span>Production Engineering Console</span>
            </div>
            <div className="row">
              <span>Current Status</span>
              <span className="status-val-highlight">Active · Open to UK Roles</span>
            </div>
            <div className="row">
              <span>Primary Role</span>
              <span>Enterprise AI Agent &amp; Backend Engineer</span>
            </div>
            <div className="row">
              <span>Client Domain</span>
              <span>Cognizant (Major UK Retail Client)</span>
            </div>
            <div className="row">
              <span>Total Experience</span>
              <span className="mono-accent">{dynamicExp} (Since Nov 2020)</span>
            </div>
            <div className="row">
              <span>Flagship Build</span>
              <span>Multi-Agent NL-to-SQL Agent (POC)</span>
            </div>
            <div className="row">
              <span>Annual Time Reclaimed</span>
              <span className="mono-teal">250+ Hours / Year Saved</span>
            </div>
            <div className="row">
              <span>Vector Scale</span>
              <span>400+ Enterprise Database Tables</span>
            </div>
            <div className="row">
              <span>Governance &amp; Privacy</span>
              <span className="contact-spec">Zero Contact PII Exposure</span>
            </div>
          </div>
        </div>

        {/* Quantitative Impact Metric Highlights */}
        <div className="hero-metrics-bar">
          <div className="metric-badge-item">
            <div className="metric-badge-num">Nearly 6<span className="metric-badge-unit"> yrs</span></div>
            <div className="metric-badge-label">Continuous Enterprise Delivery</div>
            <div className="metric-badge-sub">Nov 2020 – Present (Cognizant UK)</div>
          </div>
          <div className="metric-badge-item">
            <div className="metric-badge-num">250+<span className="metric-badge-unit"> hrs/yr</span></div>
            <div className="metric-badge-label">Engineering Hours Saved</div>
            <div className="metric-badge-sub">Across 4 production automation engines</div>
          </div>
          <div className="metric-badge-item">
            <div className="metric-badge-num">400+<span className="metric-badge-unit"> tables</span></div>
            <div className="metric-badge-label">pgvector Semantic Indexing</div>
            <div className="metric-badge-sub">Dynamic DDL RAG schema resolution</div>
          </div>
          <div className="metric-badge-item">
            <div className="metric-badge-num">99.8%<span className="metric-badge-unit"> faster</span></div>
            <div className="metric-badge-label">Keycloak Provisioning Speedup</div>
            <div className="metric-badge-sub">Replaced 5 min/user manual tasks</div>
          </div>
        </div>

        <Pipeline />
      </div>
    </section>
  );
}
